import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Information } from 'iconsax-react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import MyApiClient from '../../../../api-client/MyApiClient';
import { sidSelectStyles } from '../../../components/Util/sidSelectStyles';
import { sports, tipologiaStruttura } from '../../../components/Util/enum';
import { useProfile } from '../../../../hooks/useProfile';
import ProductImageUploader from '../../../components/Util/ProductImageUploader';
import { VAT_OPTIONS, findVatOption, vatFieldsFromOption } from '../../../components/Util/vatOptions';
import { numeroDaTesto } from "../../../components/Util/prezzi";

/*
 * Corsi — nuovo corso: una pagina sola, con le stesse sezioni della scheda corso
 * (Informazioni, Prezzi, Condizioni). Niente percorso a passi: si salva in bozza, oppure si
 * salva e si attiva. Le squadre e la loro programmazione si aggiungono dopo, dalla scheda.
 */
export const CorsiNuovoCorso = () => {
    const navigate = useNavigate();
    const { profile: profileData } = useProfile({ autoFetch: true, includeTitles: true });

    const sdiEnabledNoVat = profileData?.club?.invoiceDataSender?.invoice_enabled === true && !profileData?.club?.invoiceDataSender?.vat_number;
    const billingNotConfirmed = profileData && profileData?.club?.invoiceDataSender?.status !== 'CONFIRMED';
    const prezzoBloccato = sdiEnabledNoVat || billingNotConfirmed;
    const motivoBlocco = sdiEnabledNoVat
        ? 'Prezzo standard e IVA non modificabili senza partita IVA configurata'
        : 'Prezzo standard e IVA modificabili dopo la validazione dei documenti fiscali';

    const [caricamento, setCaricamento] = useState(false);
    const [avviso, setAvviso] = useState(null);
    const [fotoAperta, setFotoAperta] = useState(false);
    const [sportDisponibili, setSportDisponibili] = useState([]);
    const [mostraTesserati, setMostraTesserati] = useState(false);
    const [pagamenti, setPagamenti] = useState({ stripe: false, bonifico: false });

    const [dati, setDati] = useState({
        // Nasce in bozza: con «Attivo» il corso diventa subito acquistabile
        stato: 'Bozza',
        name: '',
        sportType: '',
        maxMembers: 0,
        description: '',
        newCoursePicture: '',
        standard_price: 0,
        federated_price: 0,
        fitcoin_cashback: 0,
        vat_rate: 22,
        vat_nature: undefined,
        iva_5_percent: false,
        payable_in_club: true,
        payable_stripe: false,
        payable_bank: false,
    });

    useEffect(() => {
        const dettagli = sessionStorage.getItem('userDetails');
        if (!dettagli) return;
        const utente = JSON.parse(dettagli);
        setMostraTesserati(utente.clubNature === tipologiaStruttura.ASD.value || utente.clubNature === tipologiaStruttura.SSD.value);
        setPagamenti({ stripe: !!utente.stripe_enabled, bonifico: !!utente.bank_transfer_enabled });
    }, []);

    useEffect(() => {
        if (profileData?.club?.sports) {
            const suoi = profileData.club.sports.map(s => s.sportType);
            setSportDisponibili(sports.filter(s => suoi.includes(s.value)));
        }
    }, [profileData]);

    const cambia = (modifiche) => { setDati(prev => ({ ...prev, ...modifiche })); setAvviso(null); };
    const campo = (chiave, etichetta, tipo = 'text', bloccato = false) => (
        <div title={bloccato ? motivoBlocco : undefined}>
            <Form.Label className="mb-2">{etichetta}</Form.Label>
            <Form.Control
                disabled={bloccato}
                className="mt-2"
                type={tipo}
                value={dati[chiave] ?? ''}
                onChange={(e) => cambia({ [chiave]: e.target.value })}
            />
        </div>
    );
    const interruttore = (chiave, etichetta) => (
        <div className="sid-switch-row">
            <Form.Check type="switch" id={`sw-${chiave}`} label={etichetta} checked={!!dati[chiave]}
                onChange={(e) => cambia({ [chiave]: e.target.checked })} />
        </div>
    );

    /** Il primo dato che manca per poter attivare il corso, oppure null. */
    const cosaManca = () => {
        const numero = (v) => parseFloat(String(v ?? '').replace(',', '.'));
        if (!dati.name.trim()) return 'Inserisci il nome del corso.';
        if (!dati.sportType) return 'Scegli la tipologia di sport.';
        if (!(parseInt(dati.maxMembers, 10) > 0)) return 'I partecipanti massimi devono essere più di 0.';
        if (!dati.newCoursePicture) return 'Carica la foto del corso.';
        if (!prezzoBloccato && !(numero(dati.standard_price) > 0)) return 'Il prezzo standard deve essere maggiore di 0.';
        if (mostraTesserati && !(numero(dati.federated_price) > 0)) return 'Il prezzo tesserati / soci deve essere maggiore di 0.';
        if (!(numero(dati.fitcoin_cashback) >= 0)) return 'Il cashback Fitcoin deve essere maggiore o uguale a 0.';
        if (!dati.payable_in_club && !dati.payable_stripe && !dati.payable_bank) {
            return 'Almeno uno dei metodi di pagamento deve essere attivo.';
        }
        return null;
    };

    const payload = () => {
        const utente = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const corpo = {
            clubId: utente.localId,
            name: dati.name.trim(),
            sportType: dati.sportType,
            type: 'OPEN',
            maxMembers: parseInt(dati.maxMembers, 10) || 0,
            description: dati.description,
            standard_price: numeroDaTesto(dati.standard_price) || 0,
            federated_price: numeroDaTesto(mostraTesserati ? dati.federated_price : dati.standard_price) || 0,
            member_price: numeroDaTesto(dati.standard_price) || 0,
            fitcoin_cashback: parseInt(dati.fitcoin_cashback, 10) || 0,
            vat_rate: dati.vat_rate,
            vat_nature: dati.vat_nature,
            iva_5_percent: dati.iva_5_percent,
            payable_in_club: dati.payable_in_club,
            payable_stripe: dati.payable_stripe,
            payable_bank: dati.payable_bank,
            status: 'DRAFT',
        };
        if (dati.newCoursePicture) {
            corpo.newCoursePicture = {
                content: dati.newCoursePicture,
                name: Math.random().toString(36).substring(2, 10) + Date.now().toString(36),
            };
        }
        Object.keys(corpo).forEach(k => { if (corpo[k] === '' || corpo[k] === undefined) delete corpo[k]; });
        return corpo;
    };

    const salva = async () => {
        const attiva = dati.stato === 'Attivo';
        if (!dati.name.trim()) return setAvviso({ tipo: 'danger', testo: 'Per salvare serve almeno il nome del corso.' });
        if (attiva) {
            if (billingNotConfirmed) {
                return setAvviso({
                    tipo: 'warning',
                    testo: 'Non si può attivare la vendita finché i documenti fiscali della struttura non sono validati (La mia struttura → Documenti fiscali). Puoi salvare in bozza.',
                });
            }
            const manca = cosaManca();
            if (manca) return setAvviso({ tipo: 'danger', testo: manca });
        }

        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            const apiClient = new MyApiClient();
            setCaricamento(true);
            const risposta = await apiClient.createBookingCourse(token, payload());
            const creato = risposta?.data || risposta;
            const idCorso = creato?.id;
            if (!idCorso) throw new Error('il server non ha restituito il corso creato');

            if (attiva) await apiClient.changeCourseStatus(token, idCorso, 'PURCHASABLE');
            setCaricamento(false);
            navigate(`/struttura/corsi/${idCorso}`);
        } catch (errore) {
            setCaricamento(false);
            console.error('Corsi: corso non creato', errore);
            setAvviso({ tipo: 'danger', testo: `Creazione non riuscita: ${errore?.response?.data?.detail || errore?.message || 'errore del server'}.` });
        }
    };

    const foto = dati.newCoursePicture ? `data:image/jpeg;base64,${dati.newCoursePicture}` : '';

    return (
        <>
            <Modal className="fade" show={caricamento} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status"><span className="sr-only">Attendere…</span></Spinner>
                    <div>Creazione in corso…</div>
                </Modal.Body>
            </Modal>

            <ProductImageUploader
                show={fotoAperta}
                onClose={() => setFotoAperta(false)}
                onSave={(base64) => { setFotoAperta(false); cambia({ newCoursePicture: base64 }); }}
                titolo="Immagine del corso"
            />

            {billingNotConfirmed && (
                <Alert variant="warning" className="d-flex align-items-start gap-2 text-dark">
                    <Information size="20" className="mt-1 flex-shrink-0" />
                    <div>
                        <strong>Metodo di fatturazione non configurato.</strong> Per rendere il corso acquistabile nell&apos;App SportID
                        va prima configurato in <strong>La mia struttura → Documenti fiscali</strong>. Intanto puoi salvarlo in bozza.
                    </div>
                </Alert>
            )}

            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <Row className="g-4">
                        <Col md={4}>{campo('name', 'Nome corso')}</Col>
                        <Col md={3}>
                            <Form.Label className="mb-2">Tipologia di sport</Form.Label>
                            <Select className="mt-2" styles={sidSelectStyles} placeholder=""
                                value={sportDisponibili.filter(s => s.value === dati.sportType).map(s => ({ value: s.value, label: s.label }))[0] || null}
                                options={sportDisponibili.map(s => ({ value: s.value, label: s.label }))}
                                onChange={(o) => cambia({ sportType: o?.value || '' })} />
                        </Col>
                        <Col md={2}>{campo('maxMembers', 'Partecipanti massimi', 'number')}</Col>
                        <Col md={3}>
                            <Form.Label className="mb-2">Stato</Form.Label>
                            <Select className="mt-2" styles={sidSelectStyles}
                                value={{ value: dati.stato, label: dati.stato }}
                                options={['Bozza', 'Attivo'].map(x => ({ value: x, label: x }))}
                                onChange={(o) => cambia({ stato: o.value })} />
                        </Col>
                        <Col md={12}>
                            <Form.Label className="mb-2">Descrizione</Form.Label>
                            <div className="mt-2">
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={dati.description || ''}
                                    onChange={(evento, editor) => cambia({ description: editor.getData() })}
                                />
                            </div>
                        </Col>
                        <Col md={12}>
                            <Form.Label>Foto</Form.Label>
                            <div className="sid-photo-field">
                                <div className="sid-photo-field__preview">
                                    {foto ? <img src={foto} alt="" /> : <span>Nessuna foto</span>}
                                </div>
                                <Button size="sm" variant="outline-dark" onClick={() => setFotoAperta(true)}>
                                    {foto ? 'Sostituisci foto' : 'Carica foto'}
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <h5 className="sid-form-section">Prezzi</h5>
                    <Row className="g-4">
                        <Col md={3}>{campo('standard_price', 'Prezzo standard', 'number', prezzoBloccato)}</Col>
                        {mostraTesserati && <Col md={3}>{campo('federated_price', 'Prezzo tesserati / soci', 'number')}</Col>}
                        <Col md={3}>
                            <div title={prezzoBloccato ? motivoBlocco : undefined}>
                                <Form.Label className="mb-2">IVA</Form.Label>
                                <Select className="mt-2" styles={sidSelectStyles} isDisabled={prezzoBloccato}
                                    value={findVatOption(dati)}
                                    options={VAT_OPTIONS}
                                    onChange={(o) => setDati(prev => ({ ...prev, ...vatFieldsFromOption(o) }))} />
                            </div>
                        </Col>
                        <Col md={3}>{campo('fitcoin_cashback', 'Cashback Fitcoin', 'number')}</Col>
                    </Row>

                    <h5 className="sid-form-section">Forme di pagamento</h5>
                    <div className="sid-switch-inline">
                        {interruttore('payable_in_club', 'Acquisto pagabile in struttura')}
                        {pagamenti.stripe && interruttore('payable_stripe', 'Acquisto pagabile su Stripe')}
                        {pagamenti.bonifico && interruttore('payable_bank', 'Acquisto pagabile con bonifico')}
                    </div>
                </Card.Body>
            </Card>

            {avviso && <Alert variant={avviso.tipo} className="text-dark mt-4 mb-0">{avviso.testo}</Alert>}

            <div className="d-flex justify-content-end gap-4 mt-4">
                <button type="button" className="btn btn-dark light" onClick={() => navigate('/struttura/corsi')}>Annulla</button>
                <Button className="btn btn-primary" onClick={salva}>Salva</Button>
            </div>
        </>
    );
};

export default CorsiNuovoCorso;
